import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react'
import { Upload, Image, message, Button } from 'antd'
import { LoadingOutlined, PictureOutlined } from '@ant-design/icons'
import Iconfont from '@utils/Iconfont'
import Utils from '@utils/utils'
import API from '@api/api'
import { BATCH_IMAGES_TYPE } from '@utils/CONST'
import styles from '../../../index.module.scss'
import PromptIcon from '@assets/images/svgIcon/promptIcon.svg'
import ProductInfoInput from '@view/page_v3/LeftComp/components/SmartDetailPlaceholder/components/ProductInfoInput'

const ImageItem = ({ item, index, onDelete, onSetMain, isSmartDetailPage, isMain }) => {
    const [visible, setVisible] = useState(false)

    return (
        <div className={styles.ImageCell} style={{ width: 128 }}>
            <img src={Utils.getImgThumbnail(item.url)} style={{ width: 128, height: 128 }} alt="" />
            <div className={styles.maskBox}>
                <p onClick={() => setVisible(true)}>
                    <Iconfont className={styles.Icon} type="icon-FullscreenOutlined" />
                    <span>查看大图</span>
                </p>
                <p onClick={() => onDelete(index)}>
                    <Iconfont className={styles.Icon} type="icon-DeleteOutlined" />
                    <span>删除图片</span>
                </p>
                {isSmartDetailPage && !isMain && (
                    <p onClick={() => onSetMain(index)}>
                        <PictureOutlined className={styles.Icon} />
                        <span>设为主图</span>
                    </p>
                )}
            </div>
            {!isSmartDetailPage && <span className={styles.imgTitle}>{`图${index + 1}`}</span>}
            {isSmartDetailPage && isMain && (
                <div className={styles.smartDetailMainTag}>详情页主图</div>
            )}
            <Image
                style={{ display: 'none' }}
                src={Utils.getImgThumbnail(item.url)}
                preview={{
                    visible,
                    src: Utils.getImgThumbnail(item.url),
                    onVisibleChange: (value) => {
                        setVisible(value)
                    },
                }}
            />
        </div>
    )
}

const FusionWearBatch = ({ record, handleAddMore, projectId, setDataSource }) => {
    const isSmartDetailPage = record?.projectType === BATCH_IMAGES_TYPE.SMART_DETAIL_PAGE_BATCH
    const maxCount = isSmartDetailPage ? 5 : 10
    const uploadText = isSmartDetailPage ? '添加图片' : '添加穿搭商品图'
    const hasInputImages = useMemo(() => {
        return (record?.inputImages || []).some((image) => {
            if (!image) return false
            if (typeof image === 'string') return !!image
            return !!image?.url
        })
    }, [record?.inputImages])
    const showInputError = isSmartDetailPage ? !hasInputImages : !!record?.error

    const getMainImageIndex = useCallback((images = []) => {
        const markedIndex = images.findIndex((image) => image?.mainImage || image?.isMainImage)
        return markedIndex >= 0 ? markedIndex : 0
    }, [])

    const normalizeSmartDetailInputImages = useCallback((images = []) => {
        const nextImages = images
            .map((image) => ({
                url: image?.url,
                mainImage: !!(image?.mainImage || image?.isMainImage),
            }))
            .filter((image) => !!image.url)

        if (!nextImages.length) return []

        const mainIndex = nextImages.findIndex((image) => image.mainImage)
        const targetMainIndex = mainIndex >= 0 ? mainIndex : 0
        return nextImages.map((image, index) => ({
            url: image.url,
            mainImage: index === targetMainIndex,
        }))
    }, [])

    const buildNextRecord = useCallback((inputImages) => {
        if (!isSmartDetailPage) {
            return {
                ...record,
                inputImages,
                projectId,
            }
        }

        return {
            ...record,
            projectId,
            inputImages: normalizeSmartDetailInputImages(inputImages),
        }
    }, [isSmartDetailPage, normalizeSmartDetailInputImages, projectId, record])

    const handleDelete = async (index) => {
        const updatedInputImages = [...(record.inputImages || [])]
        updatedInputImages.splice(index, 1)
        const nextMainImageIndex = getMainImageIndex(updatedInputImages)
        const normalizedInputImages = updatedInputImages.map((image, imageIndex) => ({
            ...image,
            mainImage: imageIndex === nextMainImageIndex,
        }))

        const saveRes = await API.batchSaveSku([buildNextRecord(normalizedInputImages)])

        if (saveRes?.code === 0) {
            setDataSource?.((prev) => prev.map((it) => (
                it.skuId === record.skuId ? buildNextRecord(normalizedInputImages) : it
            )))
        }
    }

    const handleSetMain = async (index) => {
        const updatedInputImages = (record.inputImages || []).map((image, imageIndex) => ({
            ...image,
            mainImage: imageIndex === index,
        }))

        const saveRes = await API.batchSaveSku([buildNextRecord(updatedInputImages)])

        if (saveRes?.code === 0) {
            setDataSource?.((prev) => prev.map((it) => (
                it.skuId === record.skuId ? buildNextRecord(updatedInputImages) : it
            )))
        }
    }

    return (
        <div className={`${styles.refListBox} ${showInputError ? styles.SelectModelBtnError : ''}`} style={{ maxWidth: 672 }}>
            {record?.inputImages?.map((item, index) => (
                <ImageItem
                    key={index}
                    item={item}
                    index={index}
                    onDelete={handleDelete}
                    onSetMain={handleSetMain}
                    isSmartDetailPage={isSmartDetailPage}
                    isMain={index === getMainImageIndex(record?.inputImages || [])}
                />
            ))}
            {record?.inputImages?.length < maxCount && (
                <div className={styles.uploadItem}>
                    <Upload
                        accept="image/jpeg,image/png,image/webp,image/avif"
                        listType="picture-card"
                        showUploadList={false}
                        multiple
                        beforeUpload={(file, fileList) => {
                            handleAddMore(file, fileList, record.skuId)
                            return false
                        }}
                    >
                        <Iconfont className={styles.Icon} type="icon-general_dd" />
                        <p>{uploadText}</p>
                    </Upload>
                </div>
            )}
        </div>
    )
}

const SmartDetailPrompt = ({ record, setDataSource }) => {
    const handleSmartDetailPromptChange = useCallback((promptVal) => {
        if (!setDataSource) return
        setDataSource((prev) => prev.map((item) => {
            if (item.skuId === record.skuId) {
                return {
                    ...item,
                    prompt: promptVal,
                }
            }
            return item
        }))
    }, [record?.skuId, setDataSource])

    return (
        <div className={record?.error && !(record?.prompt || '').trim() ? styles.SelectModelBtnError : ''} style={{ borderRadius: 8 }}>
            <ProductInfoInput
                value={record?.prompt || ''}
                detailImageList={record?.inputImages || []}
                onChange={handleSmartDetailPromptChange}
            />
        </div>
    )
}

const FusionWearPromptContent = ({ record, setDataSource }) => {
    const [promptLoading, setPromptLoading] = useState(false)
    const [showPlaceholder, setShowPlaceholder] = useState(!record?.prompt)
    const contentEditableRef = useRef(null)
    const isComposing = useRef(false)
    const lastPromptRequestId = useRef(0)
    const isSmartDetailPage = false

    const KEYWORDS = useMemo(() => {
        const kws = ['模特', ...Array.from({ length: 10 }, (_, i) => `图${i + 1}`)]
        return kws.sort((a, b) => b.length - a.length)
    }, [])

    const formatText = useCallback((text) => {
        const cleanText = (text || '').replace(/【|】/g, '')
        const pattern = new RegExp(`(${KEYWORDS.join('|')})`, 'g')
        return cleanText.replace(pattern, '<span class="keyword-highlight">$1</span>')
    }, [KEYWORDS])

    const saveSelection = (containerEl) => {
        const selection = window.getSelection()
        if (selection.rangeCount === 0) return null
        const range = selection.getRangeAt(0)
        const preSelectionRange = range.cloneRange()
        preSelectionRange.selectNodeContents(containerEl)
        preSelectionRange.setEnd(range.startContainer, range.startOffset)
        const start = preSelectionRange.toString().length
        return {
            start,
            end: start + range.toString().length,
        }
    }

    const restoreSelection = (containerEl, savedSelection) => {
        if (!savedSelection) return
        let charIndex = 0
        const range = document.createRange()
        range.setStart(containerEl, 0)
        range.collapse(true)
        const nodeStack = [containerEl]
        let node
        let foundStart = false
        let stop = false

        while (!stop && (node = nodeStack.pop())) {
            if (node.nodeType === 3) {
                const nextCharIndex = charIndex + node.length
                if (!foundStart && savedSelection.start >= charIndex && savedSelection.start <= nextCharIndex) {
                    range.setStart(node, savedSelection.start - charIndex)
                    foundStart = true
                }
                if (foundStart && savedSelection.end >= charIndex && savedSelection.end <= nextCharIndex) {
                    range.setEnd(node, savedSelection.end - charIndex)
                    stop = true
                }
                charIndex = nextCharIndex
            } else {
                let i = node.childNodes.length
                while (i--) nodeStack.push(node.childNodes[i])
            }
        }

        const sel = window.getSelection()
        sel.removeAllRanges()
        sel.addRange(range)
    }

    const handleContentInput = useCallback((e) => {
        let text = e.target.innerText
        const isEmpty = text.replace(/\n/g, '').length === 0
        setShowPlaceholder(isEmpty)

        if (isComposing.current) return

        if (text.length > 800) {
            text = text.slice(0, 800)
            message.warning('最多输入800字')
        }

        if (isEmpty) {
            if (e.target.innerHTML !== '') e.target.innerHTML = ''
            return
        }

        const selection = saveSelection(e.target)
        const newHTML = formatText(text)

        if (e.target.innerHTML !== newHTML) {
            e.target.innerHTML = newHTML
            restoreSelection(e.target, selection)
        }
    }, [formatText])

    const handleCompositionStart = () => {
        isComposing.current = true
    }

    const handleCompositionEnd = (e) => {
        isComposing.current = false
        handleContentInput(e)
    }

    const getInputValue = useCallback(() => {
        if (!contentEditableRef.current) return ''
        const text = contentEditableRef.current.innerText
        const cleanText = text.replace(/【|】/g, '')
        const pattern = new RegExp(`(${KEYWORDS.join('|')})`, 'g')
        return cleanText.replace(pattern, '【$1】')
    }, [KEYWORDS])

    useEffect(() => {
        if (contentEditableRef.current) {
            const initialText = record?.prompt || ''
            if (contentEditableRef.current.innerText.replace(/【|】/g, '') !== initialText.replace(/【|】/g, '')) {
                contentEditableRef.current.innerHTML = formatText(initialText)
                setShowPlaceholder(initialText.length === 0)
            }
        }
    }, [formatText, record?.prompt])

    const handleBlur = (val) => {
        const promptVal = typeof val === 'string' ? val : getInputValue()
        if (promptVal === record.prompt) return

        if (setDataSource) {
            setDataSource((prev) => prev.map((item) => {
                if (item.skuId === record.skuId) {
                    return {
                        ...item,
                        prompt: promptVal,
                    }
                }
                return item
            }))
        }
    }

    const getAlgoPrompt = () => {
        const requestId = ++lastPromptRequestId.current
        setPromptLoading(true)
        setShowPlaceholder(false)

        API.getAlgoPrompt({
            images: record?.inputImages?.map((item) => item.url) || [],
            modelImage: record?.modelList?.[0]?.url,
        }).then((res) => {
            if (requestId !== lastPromptRequestId.current) return
            if (res?.code === 0) {
                const val = res.data?.[0] || ''
                if (contentEditableRef.current) {
                    contentEditableRef.current.innerHTML = formatText(val)
                }
                handleBlur(val)
            } else {
                throw new Error(res.message)
            }
        }).catch((err) => {
            if (requestId !== lastPromptRequestId.current) return
            message.error(err.message)
        }).finally(() => {
            if (requestId === lastPromptRequestId.current) {
                setPromptLoading(false)
            }
        })
    }

    return (
        <div className={styles.inputContainer} style={{ height: 128 }}>
            {showPlaceholder && (
                <div className={styles.placeholderOverlay}>
                    {isSmartDetailPage ? '请填写商品描述，例如款式品类、工艺细节、适用场景等，或使用' : '请描述想要生成的画面，或点击'}
                    <span onClick={getAlgoPrompt} className={styles.smartPromptLink}>
                        {promptLoading ? <LoadingOutlined /> : (isSmartDetailPage ? 'AI分析' : '智能提示词')}
                    </span>
                    {!isSmartDetailPage && '自动生成'}
                </div>
            )}
            <div
                ref={contentEditableRef}
                className={styles.contenteditableWrap}
                contentEditable
                onInput={handleContentInput}
                onCompositionStart={handleCompositionStart}
                onCompositionEnd={handleCompositionEnd}
                onBlur={() => handleBlur()}
                suppressContentEditableWarning
            />
            <Button
                loading={promptLoading}
                className={styles.smartPromptBtn}
                onClick={getAlgoPrompt}
            >
                {!promptLoading && <img style={{ width: 16 }} src={PromptIcon} alt="" />}
                {isSmartDetailPage ? 'AI分析' : '智能提示词'}
            </Button>
        </div>
    )
}

export const FusionWearPrompt = ({ record, setDataSource }) => {
    if (record?.projectType === BATCH_IMAGES_TYPE.SMART_DETAIL_PAGE_BATCH) {
        return <SmartDetailPrompt record={record} setDataSource={setDataSource} />
    }

    return <FusionWearPromptContent record={record} setDataSource={setDataSource} />
}

export default FusionWearBatch
